import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Trash2, Plus, Save, X, Shield, User as UserIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'clients',   label: 'Clients' },
  { key: 'finance',   label: 'Finance' },
  { key: 'templates', label: 'Templates' },
  { key: 'history',   label: 'History' },
];

const emptyForm = () => ({
  email: '', password: '', name: '', role: 'custom', features: ['dashboard', 'clients'],
  can_assign_tasks: false,
});

const RolePill = ({ role }) => {
  const isAdmin = role === 'super_admin';
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
        isAdmin
          ? 'bg-primary/10 text-primary border border-primary/20'
          : 'bg-ink/5 text-ink/70 border border-ink/10'
      }`}
    >
      {isAdmin ? <Shield className="w-3 h-3" /> : <UserIcon className="w-3 h-3" />}
      {isAdmin ? 'Super Admin' : 'Custom'}
    </span>
  );
};

const FeatureTags = ({ role, features }) => {
  if (role === 'super_admin') return <span className="italic text-ink/40">All access</span>;
  const list = features || [];
  if (list.length === 0) return <span className="italic text-ink/40">No features</span>;
  return list.map((f) => (
    <span key={f} className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
      {FEATURES.find((x) => x.key === f)?.label || f}
    </span>
  ));
};


const UserRow = ({ u, onEdit, onDelete, currentUserId }) => {
  const isMe = u.id === currentUserId;
  return (
    <div
      data-testid={`user-row-${u.email}`}
      className="grid grid-cols-12 gap-4 px-6 py-4 items-center border-b border-mute/20 last:border-b-0"
    >
      <div className="col-span-3">
        <div className="font-medium text-ink">{u.name}</div>
        <div className="text-xs text-ink/50">{u.email}</div>
      </div>
      <div className="col-span-2"><RolePill role={u.role} /></div>
      <div className="col-span-4 text-xs text-ink/70 flex flex-wrap gap-1">
        <FeatureTags role={u.role} features={u.features} />
        {u.can_assign_tasks && u.role !== 'super_admin' && (
          <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
            Can assign tasks
          </span>
        )}
      </div>
      <div className="col-span-2 text-xs">
        {u.active === false
          ? <span className="text-red-600 font-medium">Disabled</span>
          : <span className="text-emerald-600 font-medium">Active</span>}
      </div>
      <div className="col-span-1 flex items-center justify-end gap-2">
        <button
          data-testid={`edit-user-${u.email}`}
          onClick={() => onEdit(u)}
          className="text-xs text-primary hover:underline font-medium"
        >
          Edit
        </button>
        {!isMe && (
          <button
            data-testid={`delete-user-${u.email}`}
            onClick={() => onDelete(u)}
            className="p-1.5 rounded-md text-ink/40 hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Delete user"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

const UserFormModal = ({ initial, onClose, onSaved }) => {
  const isCreate = !initial?.id;
  const [form, setForm] = useState(() => (
    initial?.id
      ? { ...initial, password: '', features: initial.features || [] }
      : emptyForm()
  ));
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleFeature = (key) => setForm((f) => ({
    ...f,
    features: f.features.includes(key)
      ? f.features.filter((x) => x !== key)
      : [...f.features, key],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isCreate) {
        const payload = {
          email: form.email.trim(),
          password: form.password,
          name: form.name.trim() || form.email,
          role: form.role,
          features: form.role === 'super_admin' ? [] : form.features,
          can_assign_tasks: !!form.can_assign_tasks,
        };
        await axios.post(`${API}/auth/users`, payload);
        toast.success('User created');
      } else {
        const payload = {
          name: form.name.trim(),
          role: form.role,
          features: form.role === 'super_admin' ? [] : form.features,
          can_assign_tasks: !!form.can_assign_tasks,
          active: form.active !== false,
        };
        if (form.password) payload.password = form.password;
        await axios.patch(`${API}/auth/users/${initial.id}`, payload);
        toast.success('User updated');
      }
      onSaved();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(typeof detail === 'string' ? detail : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-6" data-testid="user-modal">
      <div className="bg-white rounded-3xl border border-mute/30 shadow-card-hover max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-8 py-6 border-b border-mute/20">
          <h2 className="font-display text-2xl font-bold text-ink tracking-tight">
            {isCreate ? 'New User' : 'Edit User'}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-ink/5 text-ink/60">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="px-8 py-6 space-y-5">
          <div>
            <Label className="text-sm font-medium text-ink/70 mb-1.5 block">Email</Label>
            <Input
              data-testid="form-email"
              type="email"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              required
              disabled={!isCreate}
              className="h-11"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-ink/70 mb-1.5 block">Display Name</Label>
            <Input
              data-testid="form-name"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              required
              className="h-11"
            />
          </div>
          <div>
            <Label className="text-sm font-medium text-ink/70 mb-1.5 block">
              {isCreate ? 'Password' : 'New Password (leave blank to keep)'}
            </Label>
            <Input
              data-testid="form-password"
              type="password"
              value={form.password}
              onChange={(e) => setField('password', e.target.value)}
              required={isCreate}
              minLength={isCreate ? 8 : undefined}
              className="h-11"
            />
            {isCreate && <p className="text-xs text-ink/50 mt-1">Minimum 8 characters</p>}
          </div>
          <div>
            <Label className="text-sm font-medium text-ink/70 mb-1.5 block">Role</Label>
            <Select value={form.role} onValueChange={(v) => setField('role', v)}>
              <SelectTrigger data-testid="form-role" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Custom (pick features below)</SelectItem>
                <SelectItem value="super_admin">Super Admin (full access)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.role === 'custom' && (
            <div>
              <Label className="text-sm font-medium text-ink/70 mb-2 block">Feature Access</Label>
              <div className="space-y-2 bg-surface-50 rounded-2xl border border-mute/20 p-4">
                {FEATURES.map((f) => (
                  <label key={f.key} className="flex items-center gap-3 cursor-pointer text-sm text-ink">
                    <Checkbox
                      data-testid={`feature-${f.key}`}
                      checked={form.features.includes(f.key)}
                      onCheckedChange={() => toggleFeature(f.key)}
                    />
                    {f.label}
                  </label>
                ))}
              </div>
              <p className="text-xs text-ink/50 mt-2">
                Unchecked modules will be hidden from the user's menu and blocked at the API.
              </p>
            </div>
          )}

          <div>
            <Label className="text-sm font-medium text-ink/70 mb-2 block">Task Permissions</Label>
            <div className="bg-surface-50 rounded-2xl border border-mute/20 p-4">
              {form.role === 'super_admin' ? (
                <p className="text-sm text-ink/70 italic">Super Admins can always assign tasks.</p>
              ) : (
                <label className="flex items-center gap-3 cursor-pointer text-sm text-ink">
                  <Checkbox
                    data-testid="toggle-can-assign-tasks"
                    checked={!!form.can_assign_tasks}
                    onCheckedChange={(v) => setField('can_assign_tasks', !!v)}
                  />
                  Can assign tasks to other users
                </label>
              )}
            </div>
            <p className="text-xs text-ink/50 mt-2">
              The Task module is always available. This only controls whether the user can <em>delegate</em>.
            </p>

          {!isCreate && (
            <div>
              <Label className="text-sm font-medium text-ink/70 mb-2 block">Account Status</Label>
              <div className="bg-surface-50 rounded-2xl border border-mute/20 p-4">
                <label className="flex items-center gap-3 cursor-pointer text-sm text-ink">
                  <Checkbox
                    data-testid="toggle-active"
                    checked={form.active !== false}
                    onCheckedChange={(v) => setField('active', !!v)}
                  />
                  Account active — uncheck to block this user from signing in
                </label>
              </div>
            </div>
          )}

          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="h-11 px-5 rounded-full bg-ink/5 text-ink/70 text-sm font-semibold hover:bg-ink/10 transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="form-save"
              type="submit"
              disabled={saving}
              className="h-11 px-6 rounded-full bg-primary text-white text-sm font-semibold shadow-sm hover:bg-primary-hover transition-colors disabled:opacity-60 inline-flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isCreate ? 'Create User' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Users = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {} for new, user obj for edit, null for closed

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/auth/users`);
      setUsers(r.data.users || []);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.email}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/auth/users/${u.id}`);
      toast.success('User deleted');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="p-8" data-testid="users-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="text-4xl font-semibold text-ink tracking-tight">Users & Access</h1>
          <p className="text-ink/60 mt-1">{users.length} user{users.length === 1 ? '' : 's'} · manage roles and feature access</p>
        </div>
        <button
          data-testid="new-user-btn"
          onClick={() => setEditing({})}
          className="h-11 px-5 rounded-full bg-primary text-white font-semibold shadow-sm hover:bg-primary-hover transition-colors inline-flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> New User
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-mute/30 overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-surface-50 border-b border-mute/30 text-xs font-semibold tracking-wide text-ink/60">
          <div className="col-span-3">User</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-4">Features</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1"></div>
        </div>
        {loading ? (
          <div className="p-12 text-center text-ink/50">Loading…</div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-ink/50">No users yet.</div>
        ) : (
          users.map((u) => (
            <UserRow
              key={u.id}
              u={u}
              currentUserId={me?.id}
              onEdit={(usr) => setEditing(usr)}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>

      {editing !== null && (
        <UserFormModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
};

export default Users;
